/**
 * Gallery class grouping (S3, CONSULTS §1) — pure display policy over the
 * SERVED profile_class enum. Mapping served enum values to display groups
 * is presentation, not planner math (uxarch ruling): the branch decision
 * lives in article_shape.py and arrives via the payload.
 *
 * Degradation (ruled, safe direction): a missing/malformed floor payload
 * groups into 'standard'.
 */

import { ProfileClass, parseShapeFloor } from './shapeFloor';
import { GALLERY_STRINGS } from '../galleryStrings';

export type GalleryClass = 'compact' | 'standard';

/** short_form → Compact; every other served class → Standard. */
export function galleryClassFor(profileClass: ProfileClass | undefined): GalleryClass {
  return profileClass === 'short_form' ? 'compact' : 'standard';
}

/** Class display name from the SERVED profile_class, via the constants
 *  module (mapping served enum values is presentation, not planner math). */
export function classLabelFor(profileClass: ProfileClass | undefined): string {
  return galleryClassFor(profileClass) === 'compact'
    ? GALLERY_STRINGS.classes.compact.header
    : GALLERY_STRINGS.classes.standard.header;
}

/**
 * Group list items by gallery class, preserving input order within each
 * group (the caller's sort — e.g. Yours-first — is retained per class).
 */
export function groupByGalleryClass<T extends { shape_floor?: unknown }>(
  items: T[],
): Record<GalleryClass, T[]> {
  const groups: Record<GalleryClass, T[]> = { compact: [], standard: [] };
  for (const item of items) {
    const floor = parseShapeFloor(item.shape_floor);
    groups[galleryClassFor(floor?.profile_class)].push(item);
  }
  return groups;
}
